import { useSyncExternalStore } from "react";

/**
 * MED-1734: what the slow-connection banner needs to know about the network.
 *
 * Kept outside Redux on purpose: the Axios interceptors update it on every
 * request, and the error-modal tests assert that quiet requests never touch
 * `store.dispatch`. `inFlight` is the size of the AbortController registry, so
 * "Annuler" can abort everything pending without the counter drifting.
 */
export interface NetworkState {
  inFlight: number;
  slow: boolean;
  offline: boolean;
}

/** A request still pending after this long marks the connection as slow. */
export const SLOW_CONNECTION_MS = 5_000;

const initialState: NetworkState = { inFlight: 0, slow: false, offline: false };

let state: NetworkState = initialState;
const listeners = new Set<() => void>();
const pending = new Set<AbortController>();
let slowTimer: ReturnType<typeof setTimeout> | undefined;

const emit = (patch: Partial<NetworkState>) => {
  state = { ...state, ...patch };
  listeners.forEach(listener => listener());
};

const stopSlowTimer = () => {
  if (slowTimer !== undefined) clearTimeout(slowTimer);
  slowTimer = undefined;
};

const startSlowTimer = () => {
  stopSlowTimer();
  slowTimer = setTimeout(() => {
    slowTimer = undefined;
    if (pending.size > 0) emit({ slow: true });
  }, SLOW_CONNECTION_MS);
};

const requestStarted = (controller: AbortController) => {
  const wasIdle = pending.size === 0;
  pending.add(controller);
  if (wasIdle) startSlowTimer();
  emit({ inFlight: pending.size });
};

/** No-op for a request this store never saw (or already cancelled). */
const requestEnded = (controller?: AbortController) => {
  if (controller === undefined || !pending.delete(controller)) return;
  if (pending.size === 0) {
    stopSlowTimer();
    emit({ inFlight: 0, slow: false });
  } else {
    emit({ inFlight: pending.size });
  }
};

/** "Annuler": abort every in-flight request and go back to idle. */
const cancelAll = () => {
  const controllers = [...pending];
  pending.clear();
  stopSlowTimer();
  emit({ inFlight: 0, slow: false });
  controllers.forEach(controller => controller.abort());
};

const setOffline = (offline: boolean) => {
  if (offline !== state.offline) emit({ offline });
};

/** Test helper: forget every pending request and timer. */
const reset = () => {
  pending.clear();
  stopSlowTimer();
  state = initialState;
  listeners.forEach(listener => listener());
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

const getState = () => state;

export const networkSlice = {
  getState,
  subscribe,
  requestStarted,
  requestEnded,
  cancelAll,
  setOffline,
  reset,
};

export const useNetworkState = (): NetworkState =>
  useSyncExternalStore(subscribe, getState, getState);
